import { useEffect, useState } from "react";
import clsx from "clsx";
import { getErrorMessage } from "../../api/client";
import {
  ConteggiStorico,
  PaginaRichieste,
  StatoRichiestaRitiro,
  StoricoRichieste,
  elencoRichieste,
  storicoRichieste,
} from "../../api/ritiri";
import { CardRitiri, PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { giornoBreve } from "./presentazione";
import { testoEsecuzione } from "./RichiesteCassaforte";

/**
 * Scheda "Richieste" (§ 5, v1.14): elenco con filtri per stato, tipo e giorno
 * di ritiro, e lo storico per cassaforte e per trasportatore (richieste,
 * eseguite in anticipo, nei tempi, in ritardo, non effettuate). I conteggi li
 * fa il backend. L'elenco è paginato dal backend (v1.17): qui il totale e la
 * navigazione tra le pagine.
 */

const STATI: { valore: StatoRichiestaRitiro; nome: string }[] = [
  { valore: "bozza", nome: "Bozza" },
  { valore: "approvata", nome: "Approvata" },
  { valore: "inviata", nome: "Inviata" },
  { valore: "confermata", nome: "Confermata" },
  { valore: "eseguita", nome: "Eseguita" },
  { valore: "annullata", nome: "Annullata" },
];

const TIPI = [
  { valore: "suggerito", nome: "Suggerita" },
  { valore: "extra", nome: "Extra" },
  { valore: "sollecito", nome: "Sollecito" },
];

const controllo = "mt-1 h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink";
const th = "bg-ritiri-th-fondo px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo";

export function RichiesteRitiri({ onApri }: { onApri: (idMacchina: string) => void }) {
  const [filtri, setFiltri] = useState({ stato: "", tipo: "", da: "", a: "" });
  const [pagina, setPagina] = useState(1);
  const [elenco, setElenco] = useState<PaginaRichieste | null>(null);
  const [storico, setStorico] = useState<StoricoRichieste | null>(null);
  // Elenco e storico si caricano per conto loro: ognuno ha il suo errore e pulisce solo quello.
  const [erroreElenco, setErroreElenco] = useState<string | null>(null);
  const [erroreStorico, setErroreStorico] = useState<string | null>(null);
  const [tentativoStorico, setTentativoStorico] = useState(0);

  useEffect(() => {
    let valida = true;
    setElenco(null);
    elencoRichieste(filtri, { page: pagina })
      .then((r) => valida && (setElenco(r), setErroreElenco(null)))
      .catch((err) => valida && setErroreElenco(getErrorMessage(err, "Impossibile caricare le richieste.")));
    return () => {
      valida = false;
    };
  }, [filtri, pagina]);

  useEffect(() => {
    let valida = true;
    setStorico(null);
    setErroreStorico(null);
    storicoRichieste()
      .then((s) => valida && setStorico(s))
      .catch((err) => valida && setErroreStorico(getErrorMessage(err, "Impossibile caricare lo storico.")));
    return () => {
      valida = false;
    };
  }, [tentativoStorico]);

  const riprovaStorico = () => setTentativoStorico((n) => n + 1);

  // Con filtri nuovi si riparte dalla prima pagina.
  const imposta = (campo: keyof typeof filtri) => (valore: string) => {
    setFiltri((f) => ({ ...f, [campo]: valore }));
    setPagina(1);
  };
  const righe = elenco?.richieste ?? null;
  const pagine = elenco ? Math.max(1, Math.ceil(elenco.totale / elenco.limit)) : 1;

  return (
    <div className="space-y-6">
      <CardRitiri titolo="Richieste di ritiro">
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Stato
            <select className={controllo} value={filtri.stato} onChange={(e) => imposta("stato")(e.target.value)}>
              <option value="">Tutti</option>
              {STATI.map((s) => (
                <option key={s.valore} value={s.valore}>
                  {s.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Tipo
            <select className={controllo} value={filtri.tipo} onChange={(e) => imposta("tipo")(e.target.value)}>
              <option value="">Tutti</option>
              {TIPI.map((t) => (
                <option key={t.valore} value={t.valore}>
                  {t.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Ritiro dal
            <input type="date" className={controllo} value={filtri.da} onChange={(e) => imposta("da")(e.target.value)} />
          </label>
          <label className="block text-xs font-bold text-ritiri-th-testo">
            Ritiro al
            <input type="date" className={controllo} value={filtri.a} onChange={(e) => imposta("a")(e.target.value)} />
          </label>
        </div>
        {erroreElenco && (
          <p className="text-sm text-ink" role="alert">
            {erroreElenco}
          </p>
        )}
        {!righe && !erroreElenco && <p className="text-sm text-ritiri-th-testo">Caricamento…</p>}
        {elenco && elenco.totale === 0 && <p className="rounded-xl border border-ritiri-bordo px-3 py-6 text-center text-sm text-ritiri-th-testo">Nessuna richiesta.</p>}
        {/* Oltre l'ultima pagina (per esempio dopo un annullo altrove): si torna all'ultima. */}
        {elenco && elenco.totale > 0 && righe!.length === 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ritiri-bordo px-3 py-4 text-sm text-ritiri-th-testo">
            <span>Nessuna richiesta in questa pagina.</span>
            <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => setPagina(pagine)}>
              Vai all'ultima pagina
            </PulsanteRitiri>
          </div>
        )}
        {righe && righe.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-ritiri-bordo">
            <table className="w-full min-w-[720px] border-collapse text-sm" data-richieste="elenco">
              <thead>
                <tr>
                  <th scope="col" className={th}>Ritiro</th>
                  <th scope="col" className={th}>Punto</th>
                  <th scope="col" className={th}>Tipo</th>
                  <th scope="col" className={th}>Stato</th>
                  <th scope="col" className={th}>Esito del ritiro</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((q) => (
                  <tr key={q.id} className="ritiri-riga border-t border-ritiri-bordo hover:bg-ritiri-hover">
                    <td className="px-3 py-2 font-plex">{giornoBreve(q.data_ritiro_richiesta)}/{q.data_ritiro_richiesta.slice(0, 4)}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-left font-semibold text-ink underline-offset-2 hover:underline" onClick={() => onApri(q.id_macchina)}>
                        {q.nome_punto}
                      </button>
                      <span className="ml-1.5 font-plex text-xs text-ritiri-th-testo">{q.id_macchina}</span>
                    </td>
                    <td className="px-3 py-2">{TIPI.find((t) => t.valore === q.tipo)?.nome}</td>
                    <td className="px-3 py-2">{STATI.find((s) => s.valore === q.stato)?.nome}</td>
                    <td className={clsx("px-3 py-2", q.non_effettuata && "font-bold text-danger")}>
                      {q.non_effettuata ? "Ritiro non effettuato" : testoEsecuzione(q) ?? q.non_verificabile ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {elenco && elenco.totale > 0 && (
          <nav className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-ritiri-th-testo" aria-label="Pagine delle richieste" data-richieste="pagine">
            <span aria-live="polite">
              {elenco.totale === 1 ? "1 richiesta" : `${elenco.totale} richieste`} · pagina {elenco.page} di {pagine}
            </span>
            {pagine > 1 && (
              <span className="flex gap-2">
                <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" disabled={elenco.page <= 1} onClick={() => setPagina(elenco.page - 1)}>
                  Precedente
                </PulsanteRitiri>
                <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" disabled={elenco.page >= pagine} onClick={() => setPagina(elenco.page + 1)}>
                  Successiva
                </PulsanteRitiri>
              </span>
            )}
          </nav>
        )}
      </CardRitiri>

      <CardRitiri titolo="Storico per cassaforte">
        <TabellaStorico
          righe={storico?.per_cassaforte.map((r) => ({ chiave: r.id_macchina, nome: `${r.nome_punto} · ${r.id_macchina}`, c: r })) ?? null}
          intestazione="Cassaforte"
          errore={erroreStorico}
          onRiprova={riprovaStorico}
        />
      </CardRitiri>
      <CardRitiri titolo="Storico per trasportatore">
        <TabellaStorico
          righe={storico?.per_trasportatore.map((r) => ({ chiave: r.trasportatore, nome: r.trasportatore, c: r })) ?? null}
          intestazione="Trasportatore"
          errore={erroreStorico}
          onRiprova={riprovaStorico}
        />
      </CardRitiri>
    </div>
  );
}

function TabellaStorico({
  righe,
  intestazione,
  errore,
  onRiprova,
}: {
  righe: { chiave: string; nome: string; c: ConteggiStorico }[] | null;
  intestazione: string;
  errore: string | null;
  onRiprova: () => void;
}) {
  if (errore) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-storico="errore">
        <p className="text-sm text-ink" role="alert">
          {errore}
        </p>
        <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={onRiprova}>
          Riprova
        </PulsanteRitiri>
      </div>
    );
  }
  if (!righe) return <p className="text-sm text-ritiri-th-testo">Caricamento…</p>;
  if (righe.length === 0) return <p className="text-sm text-ritiri-th-testo">Nessuna richiesta inviata finora.</p>;
  const colonne: [keyof ConteggiStorico, string][] = [
    ["richieste", "Richieste"],
    ["anticipate", "In anticipo"],
    ["nei_tempi", "Nei tempi"],
    ["in_ritardo", "In ritardo"],
    ["non_effettuate", "Non effettuate"],
    ["non_effettuate_poi_eseguite", "di cui poi eseguite"],
    ["in_attesa", "In attesa"],
    ["solleciti", "Solleciti"],
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-ritiri-bordo">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr>
            <th scope="col" className={th}>{intestazione}</th>
            {colonne.map(([k, n]) => (
              <th key={k} scope="col" className={clsx(th, "text-right")}>
                {n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {righe.map((r) => (
            <tr key={r.chiave} className="border-t border-ritiri-bordo">
              <th scope="row" className="px-3 py-2 text-left font-semibold text-ink">
                {r.nome}
              </th>
              {colonne.map(([k]) => (
                <td key={k} className={clsx("px-3 py-2 text-right font-plex", k === "non_effettuate" && r.c[k] > 0 && "font-bold text-danger")}>
                  {r.c[k]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
